import type { MeResponse } from '@tvitvi/shared';
import { Link, useMatches } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { navEntryById } from '@/app/navigation';
import { homeOrganizationIdFor } from '@/auth/active-org';
import type { TKey } from '@/i18n/types';
import { IconButton } from '@/components/ui/IconButton';
import { ICONS } from '@/components/ui/icons';
import { cn } from '@/lib/cn';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { AiHeaderLink } from '@/features/ai/AiHeaderLink';

import { BetaBadge } from './BetaBadge';
import { useBreadcrumbs, usePageTitle } from './breadcrumbs';
import { UserMenu } from './UserMenu';

/**
 * The topbar names where you are. The name comes from the menu wherever the page is IN the menu,
 * so a page never has to remember to set it and the menu stays the single source of the name.
 *
 * ---------------------------------------------------------------------------------------------
 * AND WHERE THE PAGE IS NOT IN THE MENU, IT SAYS SO EXPLICITLY RATHER THAN GUESSING.
 *
 * This used to end `entry ? t(entry.labelKey) : t('settings.title')`. The fallback was true when
 * it was written — Settings was the one page with no menu entry — and it quietly became a lie as
 * pages were added. Measured 28 August 2026: `/festivals/:id` rendered the breadcrumb "Nordlys
 * Festival AS › Innstillinger", and so did `/profile`.
 *
 * Both for the same reason. `navEntryById` searches ENTRIES; `festivals` is a SECTION whose
 * entries come from the API, and `profile` lives behind the avatar rather than in the menu at all.
 * Neither resolves, and both got Settings' name.
 *
 * So a route that is not a menu entry carries `staticData.titleKey`, and a route with neither
 * renders nothing. An empty breadcrumb is a visible gap somebody fixes; a confidently wrong one
 * is a page that tells you where you are and is wrong about it.
 * ---------------------------------------------------------------------------------------------
 */
export function Topbar({
  me,
  nav,
}: {
  me: MeResponse;
  /** Hamburgeren under 900 px: om sidemenyen er framme, hvilket element den styrer, og vekslingen. */
  nav?: { open: boolean; controls: string; onToggle: () => void };
}): ReactNode {
  const { t } = useTranslation();
  const matches = useMatches();

  const navId = matches
    .map((match) => match.staticData.navId)
    .filter((value): value is string => typeof value === 'string')
    .at(-1);
  const entry = navId ? navEntryById(navId) : undefined;

  /** The name a route sets for itself, for the pages that are deliberately not in the menu. */
  const titleKey = matches
    .map((match) => (match.staticData as { titleKey?: unknown }).titleKey)
    .filter((value): value is TKey => typeof value === 'string')
    .at(-1);

  const pageTitle = usePageTitle();
  const title = pageTitle ?? (entry ? t(entry.labelKey) : titleKey ? t(titleKey) : null);

  /*
   * LEDDENE SIDEN SELV MELDTE FRA OM, når den ligger dypere enn menyen kan uttrykke.
   *
   * Atle, 11. september: «Stien til denne skal jo da være standing Ovation > artister > Bendik >
   * Demo advance eller noe sånt.» Menypunktet alene sier «Artister» og stopper der.
   *
   * Sier ingen fra, er lista tom og toppen tegner som før — hver eneste side som fantes i går ser
   * lik ut i dag.
   */
  const crumbs = useBreadcrumbs();
  const Chevron = ICONS.chevronRight;

  const bellOrganizationId = homeOrganizationIdFor(me);

  const activeOrganization = me.memberships.find(
    (membership) => membership.organizationId === me.activeOrganizationId,
  );

  return (
    <header className="glass-nav me-[var(--shell-gap)] flex h-[var(--topbar-height)] shrink-0 items-center justify-between gap-3 rounded-[var(--radius-bar)] px-4">
      {nav === undefined ? null : (
        <IconButton
          icon="menu"
          label={t(nav.open ? 'nav.closeMenu' : 'nav.openMenu')}
          aria-expanded={nav.open}
          aria-controls={nav.controls}
          onClick={nav.onToggle}
          className="-ms-2 min-[900px]:hidden"
          data-testid="nav-toggle"
        />
      )}
      <nav
        aria-label={t('nav.main')}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-xs min-[900px]:flex-initial"
      >
        {/* Det personlige huset (sak 216) har ikke et navn å stå først i stien. */}
        {activeOrganization && activeOrganization.organizationKind !== 'personal' ? (
          <>
            <span className="truncate text-subtle">{activeOrganization.organizationName}</span>
            {/* No chevron pointing at nothing: the separator belongs to the name after it. */}
            {title !== null ? (
              <Chevron aria-hidden className="size-3 shrink-0 text-subtle" />
            ) : null}
          </>
        ) : null}
        {title !== null ? (
          crumbs.length === 0 ? (
            <span className="truncate font-medium text-default">{title}</span>
          ) : (
            /* Menypunktet er FØRSTE ledd når siden har meldt fra om resten. */
            <span className="truncate text-subtle">{title}</span>
          )
        ) : null}
        {crumbs.map((crumb, index) => (
          <span key={crumb.label + String(index)} className="flex min-w-0 items-center gap-1.5">
            <Chevron aria-hidden className="size-3 shrink-0 text-subtle" />
            {/* Det siste leddet er stedet man ER, og et sted man er skal man ikke klikke på. */}
            {crumb.to === undefined || index === crumbs.length - 1 ? (
              <span
                className={cn(
                  'truncate',
                  index === crumbs.length - 1 ? 'font-medium text-default' : 'text-subtle',
                )}
              >
                {crumb.label}
              </span>
            ) : (
              <Link to={crumb.to} className="truncate text-subtle ring-focus hover:text-default">
                {crumb.label}
              </Link>
            )}
          </span>
        ))}
      </nav>
      <div className="flex shrink-0 items-center gap-1">
        <BetaBadge />
        {/*
         * KLOKKA STAAR PAA HVER SIDE, og bare naar man er i et hus: uten organisasjon finnes det
         * ingen varsler, og hver cache-noekkel begynner med organisasjonen.
         */}
        {/* I gjestemodus: personens egne varsler, i hens egen organisasjon (P10, funn C). */}
        {/* B5: «Koble til AI» — en snarvei i toppen, ikke et menypunkt (menyen er Atles). */}
        <AiHeaderLink />
        {bellOrganizationId === null ? null : (
          <NotificationBell me={me} organizationId={bellOrganizationId} />
        )}
        <UserMenu me={me} />
      </div>
    </header>
  );
}
